import { useState } from "react";
import { formatDateOnly, publicMemberSupportContact, workspaceNoun } from "../public-site/publicSiteUtils";
import { MemberPortalSectionTabs } from "./MemberPortalTabs";
import {
  memberDocumentStatusLabel,
  memberDocumentTypeLabel,
  waiverDocumentName,
  waiverExpirationRule,
  waiverIsRequired,
  waiverSignatureLabel,
  waiverStatusCopy
} from "./memberPortalUtils";

export function MemberPortalDocumentsPanel({
  applicantStatus = null,
  member = null,
  range = {}
}) {
  const workspace = workspaceNoun(range, "range");
  const [documentsSection, setDocumentsSection] = useState("release");
  const requirements = member?.review?.requirements || {};
  const waiver = requirements.waiver || applicantStatus?.membership?.reviewPolicy?.publicSignup?.waiver || {};
  const waiverConfigured = Boolean(waiver.required !== undefined || waiver.status || waiver.instructions || waiver.document || waiver.expiresInDays);
  const waiverCopy = waiverConfigured
    ? waiverStatusCopy(waiver)
    : {
        label: "Not published yet",
        detail: `The ${workspace} has not published a member release form in this portal yet.`,
        signature: "Not published"
      };
  const waiverRequired = waiverConfigured && waiverIsRequired(waiver);
  const memberDocuments = (member?.documents?.items || []).filter((document) => document?.title || document?.type || document?.url);
  const supportContact = publicMemberSupportContact(range);
  const publicContactEmail = supportContact.email;
  const publicContactPhone = supportContact.phone;

  return (
    <section className="member-portal-documents-panel" id="member-documents">
      <div className="member-portal-dashboard-header">
        <div>
          <span className="public-section-kicker">Documents</span>
          <h2>Documents and member materials</h2>
          <p>Check whether anything needs a signature, review what the {workspace} has shared, and find the right contact path when a file is missing.</p>
        </div>
      </div>

      <MemberPortalSectionTabs
        activeSection={documentsSection}
        onSelect={setDocumentsSection}
        sections={[
          { id: "release", label: "Release Form", detail: "Signature state" },
          { id: "materials", label: "Materials", detail: "Rules and prep" },
          { id: "files", label: "Files", detail: "Shared records" }
        ]}
      />

      {documentsSection === "release" && (
        <>
          <div className="member-portal-status-grid">
            <div>
              <span>Release form</span>
              <strong>{!member ? "Shown after membership connects" : waiverConfigured ? waiverCopy.label : "Not published yet"}</strong>
              <small>{!member ? "Release-form requirements will appear after this account is connected to a membership or the range publishes them." : waiverRequired ? waiverDocumentName(waiver) : "No release-form action is needed for this membership."}</small>
            </div>
            <div>
              <span>Re-sign timing</span>
              <strong>{!member ? "Not available yet" : waiverRequired ? waiverExpirationRule(waiver) : "Not required right now"}</strong>
              <small>{!member ? "Connect membership first to see signature timing." : waiverRequired ? waiverSignatureLabel(waiver) : waiverCopy.signature}</small>
            </div>
            <div>
              <span>Member files</span>
              <strong>{memberDocuments.length ? `${memberDocuments.length} on file` : "None shared yet"}</strong>
              <small>{memberDocuments.length ? `Files the ${workspace} has attached to your member record.` : `Shared files will appear here after the ${workspace} publishes them to your record.`}</small>
            </div>
          </div>
          <div className="member-portal-split-list">
            <div>
              <h3>Release form</h3>
              {!member ? (
                <div className="compact-status-row">Release-form requirements will appear after this account is connected to a membership or the {workspace} publishes them.</div>
              ) : waiverRequired ? (
                <article>
                  <strong>{waiverDocumentName(waiver)}</strong>
                  <span>{waiverCopy.detail}</span>
                  <em>{formatDateOnly(waiver.document?.effectiveAt, "Effective date not set")} / {waiverExpirationRule(waiver)}</em>
                  {waiver.document?.url && (
                    <div className="member-portal-document-actions">
                      <a className="secondary-button" href={waiver.document.url} target="_blank" rel="noreferrer">
                        View release form
                      </a>
                    </div>
                  )}
                  {waiverCopy.actionLabel ? (
                    <div className="member-portal-document-actions">
                      <a className="public-primary-link" href="#member-status">
                        {waiverCopy.actionLabel}
                      </a>
                    </div>
                  ) : (
                    <div className="member-portal-document-actions">
                      <a className="secondary-button" href="#member-status">
                        View signed release form
                      </a>
                    </div>
                  )}
                </article>
              ) : waiverConfigured ? (
                <div className="compact-status-row">This membership does not require a release form.</div>
              ) : (
                <div className="compact-status-row">No member release form has been published in this portal yet.</div>
              )}
            </div>
          </div>
        </>
      )}

      {documentsSection === "materials" && (
        <div className="member-portal-split-list">
          <div>
            <h3>Member materials</h3>
            <article>
              <strong>Before your visit</strong>
              <span>Use the current rules posted by the {workspace}. If rules are not published here yet, confirm anything important before you arrive.</span>
              <em>Visit readiness</em>
            </article>
            <article>
              <strong>Training or qualification notes</strong>
              <span>Class packets, orientation notes, and qualification prep will appear here after the {workspace} shares them with members.</span>
              <em>Shared by {workspace}</em>
            </article>
          </div>
        </div>
      )}

      {documentsSection === "files" && (
        <div className="member-portal-split-list member-portal-full-list">
          <div>
            <h3>Your shared files</h3>
            {memberDocuments.length === 0 ? (
              <div className="compact-status-row">No files have been shared to your member record yet.</div>
            ) : memberDocuments.slice(0, 8).map((document) => (
              <article key={`${document.type || "file"}-${document.title || document.url}`}>
                <strong>{document.title || memberDocumentTypeLabel(document.type)}</strong>
                <span>{memberDocumentTypeLabel(document.type)} / {memberDocumentStatusLabel(document.status)}</span>
                {document.notes && <span>{document.notes}</span>}
                <em>{formatDateOnly(document.updatedAt || document.createdAt, "Date not shown")}</em>
                {document.url && (
                  <div className="member-portal-document-actions">
                    <a className="secondary-button" href={document.url} target="_blank" rel="noreferrer">
                      Open file
                    </a>
                  </div>
                )}
              </article>
            ))}
          </div>
          <div>
            <h3>Need a document?</h3>
            <article>
              <strong>Ask the {workspace} team</strong>
              <span>Use the published contact path for replacement forms, missing files, or questions about what needs to be signed before your next visit.</span>
              <em>{publicContactEmail || publicContactPhone ? "Contact available" : "Contact details not published"}</em>
              {(publicContactEmail || publicContactPhone) && (
                <div className="member-portal-document-actions">
                  {publicContactEmail && <a className="secondary-button" href={`mailto:${publicContactEmail}`}>Email {workspace}</a>}
                  {publicContactPhone && <a className="secondary-button" href={`tel:${publicContactPhone}`}>Call {workspace}</a>}
                </div>
              )}
            </article>
          </div>
        </div>
      )}
    </section>
  );
}
